import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import { INSTITUTE } from "@/lib/content";

const SLIDES = [
  {
    id: "ksat",
    eyebrow: "Scholarship Test",
    headline: "K-SAT 2026 Scholarship Test",
    sub: "Get Scholarship Benefits on Admission",
    cta: "Register Now",
    target: "ksat-block",
    image: "/assets/ksat-banner.png",
    bg: "bg-white",
  },
  {
    id: "results",
    eyebrow: "Proven Results",
    headline: "90%+ Results Every Year",
    sub: "Join Kothari Classes Today",
    cta: "Join Now",
    target: "inquiry-form-section",
    bg: "bg-white",
    text: "text-gray-950",
    accent: "red",
    pattern: "diagonal",
  },
  {
    id: "admissions",
    eyebrow: "Admissions 2026 – 27",
    headline: "Admissions Open",
    sub: "Limited Seats Available",
    cta: "Enroll Now",
    target: "inquiry-form-section",
    image: "/assets/admission-banner.png",
    bg: "bg-gray-950",
  },
];

const PATTERNS = {
  dots: { backgroundImage: "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.18) 1px, transparent 1px)", backgroundSize: "22px 22px" },
  grid: { backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "32px 32px" },
  diagonal: { backgroundImage: "repeating-linear-gradient(45deg, transparent 0 16px, rgba(220,38,38,0.06) 16px 17px)" },
};

export default function HeroBannerSlider() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 4500);
    return () => clearInterval(timer.current);
  }, [paused]);

  const scrollTo = useCallback((targetId) => {
    const tryScroll = () => {
      const el = document.querySelector(`[data-testid="${targetId}"]`) || document.getElementById(targetId);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 100;
        window.scrollTo({ top, behavior: "smooth" });
        return true;
      }
      return false;
    };
    if (!tryScroll()) {
      navigate("/admissions");
      setTimeout(tryScroll, 600);
    }
  }, [navigate]);

  const goPrev = () => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length);
  const goNext = () => setIndex((i) => (i + 1) % SLIDES.length);

  return (
    <section
      className="relative bg-white border-b border-gray-100"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-banner-slider"
      aria-roledescription="carousel"
    >
      <div className="max-w-[1774px] mx-auto px-4 sm:px-6 lg:px-10 py-4 lg:py-6">
        <div
          className="relative w-full overflow-hidden bg-white"
          style={{ aspectRatio: "1774 / 887" }}
          data-banner-dims="1774x887"
        >
          {SLIDES.map((s, i) => {
            const active = i === index;

            // Image-based slide (pre-designed banner PNG)
            if (s.image) {
              return (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => scrollTo(s.target)}
                  aria-label={`${s.headline} — ${s.cta}`}
                  aria-hidden={!active}
                  data-testid={`hero-slide-${s.id}`}
                  className={`absolute inset-0 ${s.bg} transition-opacity duration-700 focus:outline-none ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                >
                  <img
                    src={s.image}
                    alt={s.headline}
                    loading={i === 0 ? "eager" : "lazy"}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center" }}
                    draggable={false}
                  />
                </button>
              );
            }

            // CSS/text-based slide (Results)
            const headlineColor = s.accent === "red" ? "text-red-600" : "text-white";
            const eyebrowColor = s.accent === "red" ? "text-red-600" : "text-white/80";
            const subColor = s.accent === "red" ? "text-gray-700" : "text-white/85";
            const ctaCls = s.accent === "red"
              ? "bg-red-600 text-white hover:bg-red-700"
              : (s.bg === "bg-white" ? "bg-red-600 text-white hover:bg-red-700" : "bg-white text-red-600 hover:bg-gray-100");

            return (
              <div
                key={s.id}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${SLIDES.length}`}
                aria-hidden={!active}
                data-testid={`hero-slide-${s.id}`}
                className={`absolute inset-0 ${s.bg} ${s.text} transition-opacity duration-700 ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              >
                <div className="absolute inset-0 pointer-events-none" style={PATTERNS[s.pattern]} />

                {s.bg === "bg-white" && (
                  <>
                    <div className="absolute -top-20 -right-20 w-72 h-72 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-32 -left-20 w-72 h-72 bg-red-600/5 rounded-full blur-3xl pointer-events-none" />
                  </>
                )}

                <div className="relative h-full flex items-center">
                  <div className="px-6 sm:px-10 lg:px-14 max-w-3xl">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-px ${s.accent === "red" ? "bg-red-600" : "bg-white/70"}`} />
                      <span className={`text-[10px] sm:text-[11px] font-bold tracking-[0.22em] uppercase ${eyebrowColor}`}>
                        {s.eyebrow}
                      </span>
                    </div>
                    <h2 className={`font-display mt-3 text-2xl sm:text-3xl lg:text-5xl font-black tracking-tight leading-[1] ${headlineColor}`}>
                      {s.headline}
                    </h2>
                    <p className={`mt-3 text-sm sm:text-base lg:text-lg ${subColor} leading-snug max-w-xl`}>
                      {s.sub}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4 sm:mt-5">
                      <button
                        onClick={() => scrollTo(s.target)}
                        data-testid={`hero-slide-${s.id}-cta`}
                        className={`inline-flex items-center gap-2 ${ctaCls} font-semibold px-5 py-2.5 sm:py-3 text-xs sm:text-sm transition-all rounded-none hover:-translate-y-0.5`}
                      >
                        {s.cta} <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                      <a
                        href={`tel:${INSTITUTE.phone_raw}`}
                        data-testid={`hero-slide-${s.id}-call`}
                        className={`inline-flex items-center gap-2 border ${
                          s.bg === "bg-white"
                            ? "border-gray-300 text-gray-950 hover:border-red-600 hover:text-red-600"
                            : "border-white/60 text-white hover:bg-white hover:text-gray-950"
                        } font-semibold px-4 py-2.5 sm:py-3 text-xs sm:text-sm transition-all rounded-none`}
                      >
                        <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Call
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Prev / Next */}
          <button
            onClick={goPrev}
            aria-label="Previous slide"
            data-testid="hero-slide-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 bg-white/90 hover:bg-white border border-gray-200 flex items-center justify-center text-gray-950 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={goNext}
            aria-label="Next slide"
            data-testid="hero-slide-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-9 sm:h-9 bg-white/90 hover:bg-white border border-gray-200 flex items-center justify-center text-gray-950 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-slide-dot-${i}`}
                className={`h-1.5 transition-all ${
                  i === index
                    ? "w-7 bg-red-600"
                    : `w-1.5 ${SLIDES[index].bg === "bg-white" ? "bg-gray-300" : "bg-white/40"} hover:bg-red-600/60`
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
